import {
  createContext,
  MouseEvent,
  ReactNode,
  RefObject,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import { SlideToolbar } from '@/modules/slideEditor/SlideToolbar';

interface DropdownRegistration {
  key: string;
  ref: RefObject<HTMLElement | null>;
}

interface SlideEditorToolbarContextValue {
  /** Which dropdown is currently open (null if none) */
  activeDropdown: string | null;
  /** Toggle a dropdown open/closed */
  setActiveDropdown: React.Dispatch<React.SetStateAction<string | null>>;
  /** Ref to the toolbar container element (for anchoring first-item dropdowns) */
  toolbarRef: RefObject<HTMLDivElement | null>;
  /** Register a dropdown so the capture-phase handler can route clicks to it */
  registerDropdown: (key: string, ref: RefObject<HTMLElement | null>) => void;
  /** Unregister a dropdown when it unmounts */
  unregisterDropdown: (key: string) => void;
}

const SlideEditorToolbarContext =
  createContext<SlideEditorToolbarContextValue | null>(null);

export function useSlideEditorToolbarContext() {
  const ctx = useContext(SlideEditorToolbarContext);
  if (!ctx) {
    throw new Error(
      'useSlideEditorToolbarContext must be used within a SlideEditorToolbar'
    );
  }
  return ctx;
}

interface SlideEditorToolbarProps {
  children: ReactNode;
}

/**
 * Shared toolbar container for slide editor toolbars. Provides:
 * - Consistent visual chrome (white bg, border, rounded corners, shadow)
 * - Active dropdown coordination (only one dropdown open at a time)
 * - Capture-phase click routing so switching between dropdowns works in
 *   a single click even when MUI's backdrop intercepts pointer events
 */
export function SlideEditorToolbar({ children }: SlideEditorToolbarProps) {
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const dropdownsRef = useRef<DropdownRegistration[]>([]);

  const registerDropdown = useCallback(
    (key: string, ref: RefObject<HTMLElement | null>) => {
      // Avoid duplicate registrations
      const existing = dropdownsRef.current.find((d) => d.key === key);
      if (!existing) {
        dropdownsRef.current.push({ key, ref });
      }
    },
    []
  );

  const unregisterDropdown = useCallback((key: string) => {
    dropdownsRef.current = dropdownsRef.current.filter((d) => d.key !== key);
  }, []);

  // MUI Menu/Popover renders a full-screen backdrop that intercepts pointer
  // events while open. Clicking another toolbar button while a dropdown is
  // open hits the backdrop instead of the button — closing the first menu but
  // never opening the second (requiring two clicks to switch).
  //
  // Capture-phase handlers fire before the event reaches its DOM target, and
  // React propagates portal events through the React tree (not the DOM tree),
  // so this handler fires even when the click target is the backdrop. We check
  // whether the click lands within another button's bounding rect; if so, we
  // stop propagation (preventing the backdrop from closing the first menu) and
  // switch the active dropdown directly.
  function handleToolbarClickCapture(e: MouseEvent) {
    if (!activeDropdown) return;

    for (const { key, ref } of dropdownsRef.current) {
      if (key === activeDropdown) continue;
      if (!ref.current) continue;
      const { left, right, top, bottom } = ref.current.getBoundingClientRect();
      if (
        e.clientX >= left &&
        e.clientX <= right &&
        e.clientY >= top &&
        e.clientY <= bottom
      ) {
        e.stopPropagation();
        setActiveDropdown(key);
        return;
      }
    }
  }

  const contextValue = useMemo(
    () => ({
      activeDropdown,
      setActiveDropdown,
      toolbarRef,
      registerDropdown,
      unregisterDropdown,
    }),
    [activeDropdown, registerDropdown, unregisterDropdown]
  );

  return (
    <SlideEditorToolbarContext.Provider value={contextValue}>
      <div ref={toolbarRef} onClickCapture={handleToolbarClickCapture}>
        <SlideToolbar>{children}</SlideToolbar>
      </div>
    </SlideEditorToolbarContext.Provider>
  );
}
